import { z } from 'zod';

/**
 * Wave 19 — Alertas (Central alerts hub).
 *
 * A single endpoint that fans across every accessible project and surfaces
 * the things that need human attention, sorted by severity. This is the
 * "what do I need to deal with today" page — a triage queue, not a report.
 *
 * Categories we surface:
 *   - docsExpiring       — compliance docs (ART, alvará, CNPJ) near/past expiry
 *   - overduePOs         — open POs past expected_delivery_at
 *   - pendingApprovals   — measurements/change orders awaiting an approval token
 *   - overdueMeasurements — approved measurements unpaid past their due window
 *   - stalledStages      — in-progress stages past estimated_end_date
 *
 * Severity is a triage hint: critical (act now), warning (this week),
 * info (keep an eye on). We compute it server-side from the age/impact
 * of the underlying record so the web can group and sort consistently.
 *
 * Scoping matches project_members: admins see every alert across the
 * tenant; everyone else sees alerts only for projects where they hold
 * any role.
 */

export const alertSeveritySchema = z.enum(['critical', 'warning', 'info']);
export type AlertSeverity = z.infer<typeof alertSeveritySchema>;

export const alertCategorySchema = z.enum([
  'docsExpiring',
  'overduePOs',
  'pendingApprovals',
  'overdueMeasurements',
  'stalledStages',
]);
export type AlertCategory = z.infer<typeof alertCategorySchema>;

export const alertItemSchema = z.object({
  /** Stable id: `${category}:${sourceId}` so the web can key rows. */
  id: z.string(),
  category: alertCategorySchema,
  severity: alertSeveritySchema,
  /** Project the alert is anchored to (null is reserved for tenant-wide alerts we don't emit yet). */
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Short pt-BR-agnostic label — the web translates the category, not this. */
  title: z.string(),
  /** Human-readable context: doc type, PO number, measurement period, etc. */
  detail: z.string(),
  /**
   * Days overdue/remaining from "now" — negative = past due, positive = upcoming.
   * We send the raw number so the web can format it in the user's locale.
   */
  daysDelta: z.number().int(),
  /** The underlying record id (po id, doc id, measurement id, stage id). */
  sourceId: z.string(),
  /** Deep link target — e.g. `/projects/:id/documentos`. Web router handles it. */
  href: z.string(),
});
export type AlertItem = z.infer<typeof alertItemSchema>;

export const alertsCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  critical: z.number().int().nonnegative(),
  warning: z.number().int().nonnegative(),
  info: z.number().int().nonnegative(),
  byCategory: z.object({
    docsExpiring: z.number().int().nonnegative(),
    overduePOs: z.number().int().nonnegative(),
    pendingApprovals: z.number().int().nonnegative(),
    overdueMeasurements: z.number().int().nonnegative(),
    stalledStages: z.number().int().nonnegative(),
  }),
});
export type AlertsCounts = z.infer<typeof alertsCountsSchema>;

export const alertsSummarySchema = z.object({
  counts: alertsCountsSchema,
  items: z.array(alertItemSchema),
  generatedAt: z.string(),
});
export type AlertsSummary = z.infer<typeof alertsSummarySchema>;
